/* eslint-disable jsx-a11y/anchor-is-valid */
import { Box, Skeleton } from '@mui/material';
import { saveAs } from 'file-saver';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import 'react-responsive-pagination/themes/classic.css';
import { useSearchParams } from 'react-router-dom';
import { CURRENCY, IS_SA } from '../../../../config/appConfig';
import { AppRoutes } from '../../../../constants/appRoutes';
import { currencyFormatterHelper } from '../../../../helpers/currencyFormatterHelper';
import { DateHelper } from '../../../../helpers/dateHelper';
import { buildLocalizeRoute } from '../../../../helpers/languageHelper';
import { BucketService } from '../../../../services/bucketService';
import { SummaryCard } from '../../../../shared/components/common/summaryCard';
import { Toaster } from '../../../../shared/components/common/toaster';
import { ButtonWrapper } from '../../../../shared/components/formUtilities/useButton';
import DataTableGrid from '../../../../shared/components/formUtilities/useTable';
import { TextWrapper } from '../../../../shared/components/formUtilities/useTextWrapper';
import { InvestorService } from '../../investorService';
import { CancelOrderDialog } from './cancelOrderDialog';
import {
	InitializeMyordersColumns,
	InitializeRatiosColumns,
} from './portfolioColumns';
import { PortfolioService } from './portfolioService';


const NewTableStyleContainer = ({ children }) => {
	const { t } = useTranslation();
	return (
		<Box sx={{
			'& .MuiPaper-root': {
				boxShadow: 'none',
				borderRadius: 0,
				backgroundColor: 'transparent',
			},
			'& .MuiToolbar-root': {
				backgroundColor: 'transparent',
			},
			'& .MuiTablePagination-root': {
				borderTop: 'none',
				backgroundColor: 'transparent',
			},
			'& .MuiTablePagination-toolbar': {
				justifyContent: 'flex-end',
				paddingLeft: 0,
				paddingRight: '12px',
			},
			'& .MuiTablePagination-spacer': {
				display: 'none',
			},
			'& .MuiTablePagination-actions': {
				display: 'flex',
				alignItems: 'center',
				gap: '4px',
				marginLeft: '8px',
			},
			'& .MuiTablePagination-actions button': {
				border: '1.5px solid #063236',
				borderRadius: '20px',
				margin: '0',
				padding: '0px 4px',
				color: '#063236',
				fontWeight: 700,
				fontSize: '12px',
			},
			'& .MuiTablePagination-actions button.Mui-disabled': {
				borderColor: '#ccc',
				color: '#ccc',
			},
			'& .MuiTablePagination-actions button:nth-of-type(2)::after': {
				content: `"${t('previous').toUpperCase()}"`,
				marginLeft: '2px',
				fontSize: '12px',
				fontWeight: 700,
				letterSpacing: '0.5px',
			},
			'& .MuiTablePagination-actions button:nth-of-type(3)::before': {
				content: `"${t('next').toUpperCase()}"`,
				marginRight: '2px',
				fontSize: '12px',
				fontWeight: 700,
				letterSpacing: '0.5px',
			},
			'& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
				color: '#063236',
				fontWeight: 400,
				fontSize: '13px',
			},
			'& .MuiTablePagination-select': {
				border: '1.5px solid #063236',
				borderRadius: '10px',
				padding: '0px 24px 0px 12px',
				fontWeight: 700,
				color: '#063236',
			},
			'& table': {
				tableLayout: 'auto !important',
				width: '100% !important',
			},
			'& .MuiTableHead-root th': {
				color: '#063236',
				fontWeight: 700,
				fontSize: '12px',
				padding: '8px 12px',
				borderBottom: 'none',
				whiteSpace: 'nowrap !important',
				overflow: 'visible !important',
				minWidth: 'unset !important',
				width: 'auto !important',
			},
			'& .MuiTableHead-root th .data-table-header': {
				whiteSpace: 'nowrap !important',
				overflow: 'visible !important',
				textOverflow: 'unset !important',
				width: 'auto !important',
				maxWidth: 'none !important',
			},
			'& .MuiTableHead-root th span, & .MuiTableHead-root th div': {
				overflow: 'visible !important',
				textOverflow: 'unset !important',
				whiteSpace: 'nowrap !important',
			},
			'& .MuiTableRow-head': { backgroundColor: '#dbe1e2', borderRadius: '20px', boxShadow: 'none' },
			'& .MuiTableRow-head .MuiTableCell-head': { backgroundColor: 'inherit', padding: '16px 22px' },
			'& .MuiTableRow-head .MuiTableCell-head:first-of-type': { borderTopLeftRadius: '20px', borderBottomLeftRadius: '20px' },
			'& .MuiTableRow-head .MuiTableCell-head:last-of-type': { borderTopRightRadius: '20px', borderBottomRightRadius: '20px' },
			'& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)': { backgroundColor: '#f3f5f5' },
			'& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': { backgroundColor: '#FFFFFF' },
			'& .MuiTableBody-root .MuiTableCell-body': { backgroundColor: 'inherit', borderBottom: 'none', color: '#063236', padding: '16px 22px', fontSize: '12px', whiteSpace: 'nowrap', width: 'auto' },
			'& .MuiTableRow-root': { borderRadius: '20px' },
			'& .MuiTableBody-root .MuiTableCell-body:first-of-type': { borderTopLeftRadius: '20px', borderBottomLeftRadius: '20px' },
			'& .MuiTableBody-root .MuiTableCell-body:last-of-type': { borderTopRightRadius: '20px', borderBottomRightRadius: '20px' },
			'& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-body': { backgroundColor: 'inherit' },
		}}>
			{children}
		</Box>
	)
}

export const Portfolio = () => {
	const { t } = useTranslation();
	const portfolioService = new PortfolioService();
	const investorService = new InvestorService();
	const bucketService = new BucketService();
	const [orders, setOrders] = useState([]);
	const [summary, setSummary] = useState({});
	const [activeInvestmentCount, setActiveInvestmentCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [comments, setComments] = useState([]);
	const [loans, setLoans] = useState([]);
	const [investmentsTabIndex, setInvestmentsTabIndex] = useState(0);
	const [orderRow, setOrderRow] = useState(null);
	const [loanPagination, setLoanPagination] = useState({
		currentPage: 1,
		pageCount: 0,
		perPage: 10,
		totalCount: 0,
	});
	const [commentPagination, setCommentPagination] = useState({
		currentPage: 1,
		pageCount: 0,
		perPage: 3,
		totalCount: 0,
	});
	// eslint-disable-next-line no-unused-vars
	const [searchParams, setSearchParams] = useSearchParams();
	const tabs = [
		{ name: t('activeInvestments'), status: 'active' },
		{ name: t('pastInvestments'), status: 'past' },
	];

	const orderColumns = IS_SA ? InitializeMyordersColumns(t, (row) => {
		if (IS_SA) {
			setOrderRow(row?.original);
		}
	}) : InitializeMyordersColumns(t).filter(resp => resp.accessorKey !== 'actions');
	const [downloadingLoanIds, setDownloadingLoanIds] = useState(() => new Set());
	const onDownloadRepaymentSchedule = (loanId) => {
		setDownloadingLoanIds((prev) => new Set(prev).add(loanId));
		portfolioService
			.exportRepaymentSchedule(loanId)
			.then((file) => {
				const date = DateHelper.formatDate(new Date());
				saveAs(file.data, `Repayment-Schedule-${loanId}-${date}.csv`);
			})
			.finally(() => {
				setDownloadingLoanIds((prev) => {
					const next = new Set(prev);
					next.delete(loanId);
					return next;
				});
			});
	};
	const ratiosColumns = InitializeRatiosColumns(t, onDownloadRepaymentSchedule, downloadingLoanIds);
	const [loadingLoan, setLoadingLoan] = useState(false);
	const [loadingComment, setLoadingComment] = useState(false);
	const [loadingOrders, setLoadingOrders] = useState(false);


	const [isNotification, setIsNotification] = useState(
		searchParams.get('isNotificationComment')
	);

	useEffect(() => {
		setIsNotification(searchParams.get('isNotificationComment'));
		loadTableData();
		getDashboardData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadTableData = () => {
		getOrders();
		getComments();
	};

	const getDashboardData = async () => {
		try {
			const [activeInvestmentCount, summaryData] = await Promise.all([
				bucketService.activateInvestmentsCount(),
				investorService.getInvestorsSummary(),
			]).catch((err) => {
				setLoading(false);
			});
			setLoading(false);
			setActiveInvestmentCount(activeInvestmentCount.data);
			setSummary(summaryData.data);
		} catch { }
	};

	const onCancelInvestment = () => {
		investorService.cancelInvestments(orderRow?.loanId).then(resp => {
			Toaster({ msg: t('orderCancelledSuccessfully'), isError: false });
			setOrderRow(null);
			getOrders();
		}).catch(err => { setOrderRow(null); });
	};


	const getOrders = () => {
		setLoadingOrders(true);
		portfolioService
			.getOrders()
			.then((resp) => {
				setLoadingOrders(false);
				setOrders(resp.data);
			})
			.catch((err) => {
				setLoadingOrders(false);
			});
	};

	const getComments = (
		currentPage = commentPagination.currentPage,
		perPage = commentPagination.perPage
	) => {
		setLoadingComment(true);
		portfolioService
			.getComments(currentPage, perPage)
			.then((resp) => {
				setLoadingComment(false);
				setCommentPagination({
					pageCount: resp.data.paging?.pageCount,
					currentPage: resp.data.paging?.currentPage,
					perPage: commentPagination.perPage,
					totalCount: resp.data.paging?.totalCount,
				});
				setComments(resp.data?.list);
				rearrangeComments(resp.data?.list);
				if (isNotification === 'true') {
					setTimeout(() => {
						document
							.getElementById('isNotification')
							.scrollIntoView({ behavior: 'smooth' });
					}, [300]);
				}
			})
			.catch((err) => {
				setLoadingComment(false);
			});
	};

	const getPastComments = (
		currentPage = commentPagination.currentPage,
		perPage = commentPagination.perPage
	) => {
		setLoadingComment(true);
		portfolioService
			.getPastComments(currentPage, perPage)
			.then((resp) => {
				setLoadingComment(false);
				setCommentPagination({
					pageCount: resp.data.paging?.pageCount,
					currentPage: resp.data.paging?.currentPage,
					perPage: commentPagination.perPage,
					totalCount: resp.data.paging?.totalCount,
				});
				setComments(resp.data?.list);
				rearrangeComments(resp.data?.list);
			})
			.catch((err) => {
				setLoadingComment(false);
			});
	};

	const getLoans = (
		currentPage = loanPagination.currentPage,
		perPage = loanPagination.perPage
	) => {
		setLoadingLoan(true);
		portfolioService
			.getPortfolioLoans(currentPage, perPage)
			.then((resp) => {
				setLoadingLoan(false);
				setLoans(resp.data?.list);
				setLoanPagination({
					pageCount: resp.data.paging?.pageCount,
					currentPage: resp.data.paging?.currentPage,
					perPage: resp.data.paging?.perPage,
					totalCount: resp.data.paging?.totalCount,
				});
				reArrangeLonsBasedOnStatus(resp.data?.list);
			})
			.catch((err) => {
				setLoadingLoan(false);
			});
	};

	const getPastLoans = (
		currentPage = loanPagination.currentPage,
		perPage = loanPagination.perPage
	) => {
		setLoadingLoan(true);
		portfolioService
			.getPortfolioPastLoans(currentPage, perPage)
			.then((resp) => {
				setLoadingLoan(false);
				setLoans(resp.data?.list);
				setLoanPagination({
					pageCount: resp.data.paging?.pageCount,
					currentPage: resp.data.paging?.currentPage,
					perPage: resp.data.paging?.perPage,
					totalCount: resp.data.paging?.totalCount,
				});
				reArrangeLonsBasedOnStatus(resp.data?.list);
			})
			.catch((err) => {
				setLoadingLoan(false);
			});
	};

	const reArrangeLonsBasedOnStatus = (loans = [], status = 'Late') => {
		if (loans.length) {
			let filteredLoans = [
				...loans.filter(
					(resp) =>
						resp.loanStatus.toLocaleLowerCase() !== status.toLocaleLowerCase()
				),
				...loans.filter(
					(resp) =>
						resp.loanStatus.toLocaleLowerCase() === status.toLocaleLowerCase()
				),
			];
			setLoans(filteredLoans);
		}
	};

	const rearrangeComments = (allComments = []) => {
		let filteredComments = allComments.map((x) => ({
			...x,
			showAllNotes: false,
		}));
		setComments(filteredComments);
	};

	const getCommentNotes = (comment) => {
		if (!comment?.notes) return [];
		return comment.showAllNotes ? comment.notes : comment.notes.slice(0, 1);
	};

	const getComment = (comment) => {
		if (!comment && comment?.length) {
			return '';
		}
		return comment.charAt(comment?.length - 1) !== '.'
			? comment?.trim().concat('.')
			: comment;
	};

	useEffect(() => {
		onInvestmentTabChange(investmentsTabIndex);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [investmentsTabIndex]);

	const onInvestmentTabChange = (index) => {
		setLoanPagination({
			currentPage: 1,
			perPage: 10,
			pageCount: 0,
			totalCount: 0,
		});
		setCommentPagination({
			currentPage: 1,
			perPage: 3,
			pageCount: 0,
			totalCount: 0,
		});
		let status = tabs[index].status;
		getLoanByStatus(status, 1, 10);
		getCommentsByStatus(status, 1, 3);
	};

	const getLoanByStatus = (
		status,
		currentPage = loanPagination.currentPage,
		perPage = loanPagination.perPage
	) => {
		if (status === 'past') {
			getPastLoans(currentPage, perPage);
		} else if (status === 'active') {
			getLoans(currentPage, perPage);
		}
	};

	const onLoanPageSizeChange = (e) => {
		let status = tabs[investmentsTabIndex].status;
		getLoanByStatus(status, 1, e);
	};

	const onLoanPageChange = (e) => {
		let status = tabs[investmentsTabIndex].status;
		getLoanByStatus(status, e + 1, loanPagination.perPage);
	};

	const onCommentPageChange = (page) => {
		let status = tabs[investmentsTabIndex].status;
		getCommentsByStatus(status, page, commentPagination.perPage);
	};

	const getCommentsByStatus = (
		status,
		currentPage = commentPagination.currentPage,
		perPage = commentPagination.perPage
	) => {
		if (status === 'past') {
			getPastComments(currentPage, perPage);
		} else if (status === 'active') {
			getComments(currentPage, perPage);
		}
	};

	const toggleSeeMore = (index) => {
		comments[index].showAllNotes = !comments[index].showAllNotes;
		setComments([...comments]);
	};





	return (
		<Box className="flex flex-col gap-16 w-full">

			{/* Summary Hero Banner */}
			{!loading ? (
				<Box className="text-3xl md:text-4xl font-black text-investor-deep leading-snug mt-24">
					{t('youHaveCurrentlyInvestedIn')}{' '}
					<span className="text-investor-green underline underline-offset-4 decoration-investor-green">
						{activeInvestmentCount} {t('portfolioBusinesses')}
					</span>
					<br />
					{t('withTotalExposureOf')}{' '}
					<span className="text-investor-green underline underline-offset-4 decoration-investor-green">
						<span className="currency-symbol">{IS_SA ? 'ʢ' : CURRENCY}</span>
						{' '}{currencyFormatterHelper(summary?.outstandingAmount)}
					</span>
				</Box>
			) : (
				<Skeleton variant="rounded" height={80} sx={{ borderRadius: '16px', width: '60%' }} />
			)}

			{/* My Orders Section */}
			{orders && orders?.length ? (
				<Box>
					<Box className="text-2xl font-black text-investor-deep mb-4">
						{t('myOrders')}
					</Box>
					<Box sx={{ backgroundColor: '#f3f5f5', borderRadius: '30px', overflow: 'hidden', p: 2, pb: 0 }}>
						<NewTableStyleContainer>
							<DataTableGrid
								columnPinningLeft={isMobile ? ['businessName'] : []}
								tableClass="no-space-body"
								loading={loadingOrders}
								columns={orderColumns}
								records={orders}
								showEditColumnsButton={false}
								isPagination={false}
							/>
						</NewTableStyleContainer>
					</Box>
				</Box>
			) : null}

			{/* My Investments Section */}
			{/* Card wrapping tabs + table */}
			<Box sx={{ backgroundColor: '#f3f5f5', borderRadius: '30px', overflow: 'hidden', p: 2, pb: 0 }}>
				{/* Pill Tabs */}
				<Box className="flex gap-2 mb-4 items-center">
					{tabs.map((tab, index) => (
						<ButtonWrapper
							key={tab.status}
							label={tab.name}
							disabled={loadingLoan}
							onClick={() => setInvestmentsTabIndex(index)}
							classList={`!rounded-full !px-6 !py-2.5 !font-bold !text-sm !normal-case !border-none !min-w-0 !shadow-none ${investmentsTabIndex === index
								? '!bg-investor-deep !text-investor-green'
								: '!bg-[#0632361A] !text-investor-deep'
								}`}
							variant="outlined"
						/>
					))}
				</Box>

				{/* Investment Table */}
				<Box sx={{ overflow: 'hidden', mt: '32px' }}>
					<NewTableStyleContainer>
						<DataTableGrid
							columnPinningLeft={isMobile ? ['title'] : []}
							loading={loadingLoan}
							onPageChange={onLoanPageChange}
							onPageSizeChange={onLoanPageSizeChange}
							pagination={loanPagination}
							columns={ratiosColumns}
							records={loans}
							isPagination
							showEditColumnsButton={false}
							tableClass="no-space-body"
						/>
					</NewTableStyleContainer>
				</Box>
			</Box>

			{/* Portfolio Updates / Recent Loan Comments */}
			<Box id="isNotification">
				<Box className="text-2xl font-black text-investor-deep mb-4">
					{t('recentLoanComments')}
				</Box>
				<Box>
					{!loadingComment ? (
						<Box className="grid grid-flow-row gap-3">
							{comments && comments?.length ? (
								comments.map((comment, mainIndex) => {
									return (
										<SummaryCard
											key={mainIndex}
											classList="!py-8 !px-12 !rounded-[30px] !text-left"
										>
											<Box className="grid grid-flow-row gap-2">
												<span>
													<a
														className="!font-bold !text-investor-green !text-base hover:underline transition-colors"
														href={buildLocalizeRoute(
															`${AppRoutes.Investor}/${AppRoutes.Marketplace}/${AppRoutes.MarketplaceBusinessHistory}/${comment.loanId}`
														)}>
														{comment.businessName}
													</a>
												</span>
												{comment?.notes && comment?.notes?.length
													? getCommentNotes(comment).map(
														(note, index) => {
															return (
																<Box
																	key={index}
																	className="grid grid-flow-row gap-1">
																	<TextWrapper
																		classList="!text-sm !font-bold !text-investor-deep"
																		text={DateHelper.formatDate(
																			note.createdDate
																		)}
																	/>
																	<TextWrapper
																		classList="!text-sm !text-investor-deep !font-normal"
																		text={getComment(note.comment)}
																	/>
																</Box>
															);
														}
													)
													: null}
												{comment.notes?.length > 1 ? (
													<span>
														<a
															className="inline-flex items-center gap-1 text-investor-deep font-bold text-sm uppercase cursor-pointer bg-investor-mint/30 rounded-full py-2 pl-3 pr-6 mt-2 hover:bg-investor-mint/50 transition-colors"
															onClick={() => {
																toggleSeeMore(mainIndex);
															}}>
															<svg
																className={`w-4 h-4 transition-transform ${comment.showAllNotes ? 'rotate-180' : ''}`}
																viewBox="0 0 24 24"
																fill="none"
																stroke="currentColor"
																strokeWidth="2.5"
																strokeLinecap="round"
																strokeLinejoin="round">
																<circle cx="12" cy="12" r="10" />
																<polyline points="8 12 12 16 16 12" />
															</svg>
															{comment.showAllNotes
																? t('hideComments')
																: t('showComments')}
														</a>
													</span>
												) : null}
											</Box>
										</SummaryCard>
									);
								})
							) : (
								<SummaryCard classList="!p-12 !text-center !rounded-[30px]">
									<TextWrapper
										classList="!text-sm !text-investor-slate !w-full"
										text={t('thereAreNoComments')}
									/>
								</SummaryCard>
							)}
							{comments && comments.length ? (
								<Box className="flex items-center gap-3 mt-4">
									{commentPagination.currentPage > 1 && (
										<button
											type="button"
											className="inline-flex items-center gap-2 text-investor-deep font-bold text-sm cursor-pointer border-2 border-investor-deep rounded-full py-2 px-5 bg-transparent hover:bg-investor-deep/5 transition-colors"
											onClick={() => onCommentPageChange(commentPagination.currentPage - 1)}>
											<span>&larr;</span>
											{t('previous').toUpperCase()}
										</button>
									)}
									{commentPagination.currentPage < commentPagination.pageCount && (
										<button
											type="button"
											className="inline-flex items-center gap-2 text-investor-deep font-bold text-sm cursor-pointer border-2 border-investor-deep rounded-full py-2 px-5 bg-transparent hover:bg-investor-deep/5 transition-colors"
											onClick={() => onCommentPageChange(commentPagination.currentPage + 1)}>
											{t('next').toUpperCase()}
											<span>&rarr;</span>
										</button>
									)}
								</Box>
							) : null}
						</Box>
					) : (
						<Box className="grid grid-flow-row gap-3">
							{[1, 2, 3].map((resp) => {
								return (
									<Skeleton
										key={resp}
										variant="rounded"
										height={80}
										sx={{ borderRadius: '30px', width: '100%' }}
									/>
								);
							})}
						</Box>
					)}
				</Box>
			</Box>

			{orderRow ? <CancelOrderDialog state={orderRow} onClose={(e) => {
				if (e) {
					onCancelInvestment();
					return;
				}
				setOrderRow(null);
			}} row={orderRow} /> : null}
		</Box>
	);
};
